import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import Button from "../../components/ui/Button";
import AppImage from "../../components/AppImage";
import { Download, Briefcase, Handshake, TrendingUp, Zap, CheckCircle, Globe, Store, Award, IndianRupee, Users, MapPin, Bell, FileText, ArrowRight, ExternalLink } from "lucide-react";

const Growth = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const careers = [
    {
      title: "Sales VP",
      subtitle: "Senior Profile – Minimum 20 Years Market Experience",
      equity: "2% Equity Stake",
      salary: "₹50,000 Initial Monthly Payment",
      details: "Starting after 2 months • Salary revision after achieving 200% ROI increment",
      pdf: "Orator_Sales_Vice_President_JD.pdf",
      icon: <Users className="w-8 h-8" />,
      badgeColor: "bg-blue-100 text-blue-700"
    },
    {
      title: "Sales Operations Manager",
      subtitle: "Mid-Level Profile - Minimum 10 Years Market Experience",
      equity: "1% Equity Stake",
      salary: "₹30,000 Initial Monthly Payment",
      details: "Starting after 2 months • Salary revision after achieving 200% ROI increment",
      pdf: "Orator_Sales_Operations_Manager_JD.pdf",
      icon: <Briefcase className="w-8 h-8" />,
      badgeColor: "bg-emerald-100 text-emerald-700"
    },
    {
      title: "Service VP",
      subtitle: "Senior – Minimum 20 Years Real-World Experience",
      equity: "2% Equity Stake",
      salary: "₹50,000 Initial Monthly Payment",
      details: "Starting after 2 months • Salary revision after achieving 200% ROI increment",
      pdf: "Orator_Service_VP_JD.pdf",
      icon: <Award className="w-8 h-8" />,
      badgeColor: "bg-purple-100 text-purple-700"
    },
    {
      title: "Service Delivery Manager",
      subtitle: "Minimum 10 Years Experience",
      equity: "1% Equity Stake",
      salary: "₹30,000 Initial Monthly Payment",
      details: "Starting after 2 months • Salary revision after achieving 200% ROI increment",
      pdf: "Orator_SDM_JD.pdf",
      icon: <Users className="w-8 h-8" />,
      badgeColor: "bg-amber-100 text-amber-700"
    },
    {
      title: "VP Operations",
      subtitle: "15+ Years Industry Experience & Startup Management Expertise",
      equity: "2% Equity Stake",
      salary: "₹50,000 Initial Monthly Payment",
      details: "Starting after 2 months • Salary revision after achieving 200% ROI increment",
      pdf: "Orator_VP_Operations_JD.pdf",
      icon: <TrendingUp className="w-8 h-8" />,
      badgeColor: "bg-rose-100 text-rose-700"
    },
    {
      title: "Head – Admin & HR",
      subtitle: "10+ Years Relevant Experience",
      equity: "1% Equity Stake",
      salary: "₹30,000 Initial Monthly Payment",
      details: "Starting after 2 months • Salary revision after achieving 200% ROI increment",
      pdf: "Orator_Head-Admin_HR_JD.pdf",
      icon: <Users className="w-8 h-8" />,
      badgeColor: "bg-indigo-100 text-indigo-700"
    },
  ];

  const features = [
    { icon: <FileText className="w-6 h-6" />, text: "Call-sheet automation" },
    { icon: <Users className="w-6 h-6" />, text: "Crew CRM" },
    { icon: <MapPin className="w-6 h-6" />, text: "GPS tracking" },
    { icon: <Bell className="w-6 h-6" />, text: "Automated alerts" },
    { icon: <FileText className="w-6 h-6" />, text: "Daily summaries" },
    { icon: <CheckCircle className="w-6 h-6" />, text: "Expense approvals" },
  ];

  return (
    <>
      <Helmet>
        <title>Careers, Partnerships & Investment Opportunities - Orator Hype</title>
        <meta name="description" content="Explore career opportunities, partnership programs, and investment options with Orator Hype. Join our team or grow with us through strategic collaborations and investments." />
        <meta name="keywords" content="careers Orator Hype, partnerships, investment opportunities, job openings, branding partners, strategic investments" />
        <meta property="og:title" content="Careers, Partnerships & Investment Opportunities - Orator Hype" />
        <meta property="og:description" content="Providing structured opportunities for professionals, partners, and investors to grow with Orator." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://oratorhype.com/growth" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />

        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative py-16 md:py-24 lg:py-32 overflow-hidden text-white">
             <div
    className="absolute inset-0 bg-cover bg-center"
    style={{ backgroundImage: "url('/hero_section/Growth_Hub.png')" }}
  />
            
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
              <div className="inline-flex items-center justify-center gap-3 mb-6 md:mb-8 px-4 md:px-6 py-2 md:py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-yellow-300" />
                <span className="text-xs md:text-sm font-medium uppercase tracking-wider">Growth Hub</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-6 md:mb-8 leading-tight tracking-tight px-4">
                Careers, Partnerships &<br className="hidden md:block" /> Investment Opportunities
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-8 md:mb-12 max-w-3xl lg:max-w-4xl mx-auto px-4 opacity-90 font-light">
                Structured pathways for talented professionals, strategic partners, and visionary investors to thrive with Orator Hype.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center px-4">
                <Button asChild size="lg" className="bg-white text-indigo-900 hover:bg-gray-100 shadow-xl px-6 md:px-10 py-4 md:py-6 text-base md:text-lg font-medium">
                  <a href="/about">
                    <Briefcase className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
                    Learn About Us
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/50 text-white hover:bg-white/10 backdrop-blur-md px-6 md:px-10 py-4 md:py-6 text-base md:text-lg font-medium">
                  <a href="https://wa.me/7304660050?text=Hello%20Orator%20Hype%20Growth%20Hub" target="_blank" rel="noopener noreferrer">
                    <svg className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.76.982.998-3.675-.236-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.9 6.994c-.004 5.45-4.438 9.88-9.888 9.88m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.333.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.333 11.893-11.893 0-3.18-1.24-6.162-3.495-8.411"/></svg>
                    Contact on WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Careers Section */}
          <section className="py-12 md:py-20 lg:py-28  bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10 md:mb-16 max-w-3xl mx-auto px-4">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
                  <Briefcase className="inline-block w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mr-2 md:mr-4 text-indigo-600" />
                  Career Opportunities
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-white leading-relaxed">
                  Equity-backed roles designed for experienced leaders ready to drive exponential growth.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {careers.map((job, index) => (
                  <div
                    key={index}
                    className="group relative bg-white rounded-xl md:rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/0 to-purple-50/0 group-hover:from-indigo-50/50 group-hover:to-purple-50/30 transition-all duration-500"></div>
                    <div className="relative p-5 md:p-6 lg:p-8">
                      <div className="flex items-center justify-between mb-4 md:mb-6">
                        <div className={`p-3 md:p-4 rounded-xl md:rounded-2xl ${job.badgeColor} shadow-sm md:shadow-md`}>
                          {job.icon}
                        </div>
                        <span className="px-3 py-1 md:px-4 md:py-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 font-bold rounded-full text-xs md:text-sm">
                          {job.equity}
                        </span>
                      </div>

                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3 group-hover:text-indigo-700 transition-colors">
                        {job.title}
                      </h3>
                      <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                        {job.subtitle}
                      </p>

                      <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                        <div className="flex items-center gap-2 md:gap-3">
                         <IndianRupee className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
                          <span className="font-semibold text-gray-800 text-sm md:text-base">{job.salary}</span>
                        </div>
                        <div className="flex items-start gap-2 md:gap-3">
                          <Zap className="w-5 h-5 md:w-6 md:h-6 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-xs md:text-sm text-gray-600 leading-relaxed">{job.details}</span>
                        </div>
                      </div>

                      {/* View PDF in new tab */}
                      <a
                        href={`/pdfs/${job.pdf}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl px-4 py-3 md:px-6 md:py-4 rounded-lg md:rounded-xl font-medium text-sm md:text-base lg:text-lg flex items-center justify-center group transition-all duration-300"
                      >
                        <ExternalLink className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
                        View Job Description PDF
                        <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 md:ml-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Partner & Investment Section */}
          <section className="py-12 md:py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-12 max-w-7xl mx-auto">
                {/* Partner Section */}
                <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-6 md:p-8 lg:p-10 border border-gray-100 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center mb-6 md:mb-8">
                    <div className="p-3 md:p-4 bg-purple-100 rounded-xl md:rounded-2xl mr-4 md:mr-6">
                      <Handshake className="w-8 h-8 md:w-10 md:h-10 text-purple-600" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">PARTNER</h2>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-700 mt-1">Branding Partner Programs</h3>
                    </div>
                  </div>

                  <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 md:mb-8 lg:mb-10 leading-relaxed">
                    Earn <span className="font-bold text-emerald-600">₹25,000 – ₹4,00,000</span> monthly (plus taxes) through digital and on-ground branding collaborations.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 lg:gap-8 mb-8 md:mb-10">
                    <div className="flex items-start gap-3 md:gap-4 p-4 md:p-5 lg:p-6 bg-blue-50 rounded-xl md:rounded-2xl">
                      <Globe className="w-6 h-6 md:w-8 md:h-8 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1 text-sm md:text-base lg:text-lg">Online Partnerships</h4>
                        <p className="text-gray-600 text-xs md:text-sm lg:text-base">Digital platform collaborations</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 md:gap-4 p-4 md:p-5 lg:p-6 bg-emerald-50 rounded-xl md:rounded-2xl">
                      <Store className="w-6 h-6 md:w-8 md:h-8 text-emerald-600 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-bold text-gray-900 mb-1 text-sm md:text-base lg:text-lg">Offline Partnerships</h4>
                        <p className="text-gray-600 text-xs md:text-sm lg:text-base">On-ground branding opportunities</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    <a
                      href="/pdfs/Orator_Updating_Instagram.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 md:px-6 md:py-4 bg-white border-2 border-blue-200 hover:bg-blue-50 text-blue-700 font-medium rounded-lg md:rounded-xl hover:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md text-sm md:text-base"
                    >
                      <ExternalLink className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
                      Check Eligibility 
                    </a>
                    <a
                      href="/pdfs/Orator_Display_Branding.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 md:px-6 md:py-4 bg-white border-2 border-emerald-200 hover:bg-emerald-50 text-emerald-700 font-medium rounded-lg md:rounded-xl hover:border-emerald-300 transition-all duration-200 shadow-sm hover:shadow-md text-sm md:text-base"
                    >
                      <ExternalLink className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
                       Download the Official Invitation
                    </a>
                  </div>
                </div>

                {/* Investment Section */}
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl md:rounded-3xl shadow-xl p-6 md:p-8 lg:p-10 border border-indigo-100 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center mb-6 md:mb-8">
                    <div className="p-3 md:p-4 bg-indigo-100 rounded-xl md:rounded-2xl mr-4 md:mr-6">
                      <TrendingUp className="w-8 h-8 md:w-10 md:h-10 text-indigo-600" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">INVESTMENT</h2>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-700 mt-1">Investment Opportunities</h3>
                    </div>
                  </div>

                  <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 md:mb-8 leading-relaxed">
                    Strategic investments for sustainable growth with indicative returns up to <span className="font-bold text-emerald-600">14%</span> annually.
                  </p>

                  <div className="bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-6 md:p-8 mb-8 md:mb-10 text-center border border-indigo-200">
                    <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-emerald-600 mb-2 md:mb-3">Up to 14%</div>
                    <p className="text-base md:text-lg text-gray-700">Annual Return Potential</p>
                    <div className="flex flex-wrap justify-center gap-3 md:gap-4 lg:gap-6 mt-4 md:mt-6 text-xs sm:text-sm text-gray-600 font-medium">
                      <span>Low Risk</span>
                      <span className="text-indigo-400">•</span>
                      <span>Long-term Growth</span>
                      <span className="text-indigo-400">•</span>
                      <span>Sustainable</span>
                    </div>
                  </div>

                  <a
                    href="/pdfs/Orator_Investment–Proposal FY 2026–27-1.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-xl hover:shadow-2xl px-4 py-3 md:px-6 md:py-4 rounded-lg md:rounded-xl font-semibold text-sm md:text-base lg:text-lg flex items-center justify-center transition-all duration-300"
                  >
                    <ExternalLink className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 mr-2 md:mr-3" />
                     Download the Official Invitation
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Upcoming Projects */}
          <section className="py-12 md:py-20 lg:py-28 bg-gray-900 text-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-10 md:mb-16 max-w-4xl mx-auto px-4">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
                  <Zap className="inline-block w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mr-2 md:mr-4 text-yellow-400" />
                  Upcoming Production Automation Projects
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed">
                  Advanced automation tools built for high-volume, scalable production management.
                </p>
              </div>

              <div className="max-w-7xl mx-auto bg-gradient-to-br from-[#2642B2] via-indigo-900 to-slate-900 rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden">
                <div className="p-6 md:p-10 lg:p-16">
                  <div className="flex flex-col lg:flex-row gap-8 md:gap-10 lg:gap-12 items-center">
                    <div className="w-full lg:w-1/2">
                      <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                        OiX CRM-B1
                        <span className="block text-base sm:text-lg md:text-xl text-gray-400 mt-1 md:mt-2 font-normal">Powered by Orator Hype PVT LTD</span>
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-10">
                        {features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-3 md:gap-4">
                            <div className="p-2 md:p-3 bg-indigo-500/20 rounded-lg md:rounded-xl">
                              {feature.icon}
                            </div>
                            <span className="font-medium text-sm md:text-base lg:text-lg">{feature.text}</span>
                          </div>
                        ))}
                      </div>

                      <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                        Revolutionizing production management with cutting-edge, scalable automation solutions.
                      </p>
                    </div>

                    <div className="w-full lg:w-1/2 relative">
                      <div className="absolute inset-0 bg-gradient-to-tr from-indigo-600/20 to-purple-600/20 rounded-xl md:rounded-2xl blur-2xl md:blur-3xl"></div>
                      <AppImage
                        src="/hero_section/CRM IMG.png"
                        alt="OiX CRM-B1 Poster"
                        className="relative z-10 rounded-xl md:rounded-2xl shadow-2xl w-full h-auto max-w-md mx-auto lg:max-w-full hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Growth;